import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import type {SocialPost} from '@Components/social-media/post.vo';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import {Text, TextSize} from '@Components/text';
import ceil from 'lodash/ceil';
import {Icon} from '@Components/icon-v2';
import type {FlatIconType} from '@Components/icon-v2/icon.types';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {AllMetricKeys, FacebookVariant, InstagramVariant, SocialPlatformMetrics} from '@Components/social-media/social-platform-metrics.types';
import {
  metricsAggregate,
  metricsFacebook,
  metricsFacebookStory,
  metricsGoogleBusinessProfile,
  metricsInstagramPost,
  metricsInstagramStory,
  metricsInstagramVideo,
  metricsLinkedInPage,
  metricsPinterest,
  metricsThreads,
  metricsTikTok,
  metricsTwitter,
  metricsYouTube,
} from '@Components/social-media/social-platform-metrics.types';
import {
  getBaseIconTooltipByIndex,
  getBaseMetricDescriptionByIndex,
  getBaseMetricIconByIndex,
  getFacebookIconTooltipByIndex,
  getFacebookMetricDescriptionByIndex,
  getFacebookMetricIconByIndex,
  getFacebookMetricsByIndex,
  getGoogleBusinessProfileIconTooltipByIndex,
  getGoogleBusinessProfileMetricDescriptionByIndex,
  getGoogleBusinessProfileMetricIconByIndex,
  getGoogleBusinessProfileMetricsByIndex,
  getInstagramDescriptionByIndex,
  getInstagramIconTooltipByIndex,
  getInstagramMetricIconByIndex,
  getInstagramMetricsByIndex,
  getInstagramMetricsKey,
  getLinkedInIconTooltipByIndex,
  getLinkedInMetricDescriptionByIndex,
  getLinkedinMetricIconByIndex,
  getLinkedinMetricsByIndex,
  getPinterestIconTooltipByIndex,
  getPinterestMetricDescriptionByIndex,
  getPinterestMetricIconByIndex,
  getPinterestMetricsByIndex,
  getReadableMetrics,
  getThreadsIconTooltipByIndex,
  getThreadsMetricDescriptionByIndex,
  getThreadsMetricIconByIndex,
  getThreadsMetricsByIndex,
  getTikTokIconTooltipByIndex,
  getTikTokMetricDescriptionByIndex,
  getTikTokMetricIconByIndex,
  getTikTokMetricsByIndex,
  getTooltipReadableMetrics,
  getTwitterIconTooltipByIndex,
  getTwitterMetricDescriptionByIndex,
  getTwitterMetricIconByIndex,
  getTwitterMetricsByIndex,
  getYouTubeIconTooltipByIndex,
  getYouTubeMetricDescriptionByIndex,
  getYouTubeMetricIconByIndex,
  getYouTubeMetricsByIndex,
  isMetricPending,
  PENDING_METRIC_PLACEHOLDER,
} from '@Components/social-media/social-media-metrics-library';
import {getReadableAccounts, SocialPlatform} from '@Components/social-media/social-media.types';
import {useUserGuidelineType} from '@Panels/social-posts-dialog-panel/social-posts-dialog-panel.hooks';
import {useAppDispatch, useAppSelector} from '@/hooks';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {updatePostAfterMetricsRefresh} from '@Panels/social-posts-dialog-panel/social-posts-dialog-panel.reducer';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {getDateFromUnixTimestamp, getTimeToOrFrom} from '@Utils/date.util';
import {
  addConnectAccountBroadcastChannel,
  getAccountConnectionResponseFromBroadcast,
  handleAccountConnectionResponse,
  mapIndexToAccountPublishingParams,
  reAuthenticateInModal,
  removeConnectAccountBroadcastChannel,
} from '@Libraries/social-media';
import styles from './social-post-dialog-metrics-grid.module.scss';
import {LoadingStates} from '@Utils/loading.util';
import {ManualMetricsRefreshResult, MetricsAvailability} from '@Components/social-media/social-platform-metrics.types';
import {SocialPostMetricsPerAccountBreakdown} from '@Components/social-media/components/social-post-metrics-per-account-breakdown';
import {
  countAccountsWithAnyMetrics,
  getMetricsForAccount,
  getPlatformsForAvailability,
  hasMetricsForAccountMediaType,
  sumBaseMetricForAccount,
  sumBaseMetricsAcrossAllAccounts,
} from '@Components/social-media/social-post-metrics.helpers';
import {SkeletonBrick} from '@Components/skeleton-brick';
import type {EnablePostMetricsResponse} from './social-post-dialog-metrics-grid.types';

const REFRESH_RESULT_FLASH_DURATION_MS = 5000;
const EMPTY_METRICS = Object.freeze({}) as SocialPlatformMetrics;

interface ManualMetricsRefreshResponse {
  result: ManualMetricsRefreshResult;
  cooldownSecondsRemaining: number;
  post: SocialPost | null;
}

interface SocialPostDialogMetricsGridProps {
  activeAccountIndex: number;
  socialPost: SocialPost;
}

export enum SocialPostUserGuidelines {
  PERFORMANCE_LIMITATIONS = 'PERFORMANCE_LIMITATIONS',
  STORY_PERFORMANCE_LIMITATIONS = 'STORY_PERFORMANCE_LIMITATIONS',
  STORY_INSIGHTS_EXPIRED = 'STORY_INSIGHTS_EXPIRED',
  INSIGHTS_WINDOW_CLOSED = 'INSIGHTS_WINDOW_CLOSED',
  POPULATING = 'POPULATING',
  POPULATING_FRESH = 'POPULATING_FRESH',
  POPULATING_DELAYED = 'POPULATING_DELAYED',
  PARTIAL = 'PARTIAL',
  POST_DELETED_ON_PLATFORM = 'POST_DELETED_ON_PLATFORM',
  METRICS_TEMPORARILY_UNAVAILABLE = 'METRICS_TEMPORARILY_UNAVAILABLE',
  METRICS_UNAVAILABLE = 'METRICS_UNAVAILABLE',
  MIXED_UNAVAILABLE = 'MIXED_UNAVAILABLE',
  ERROR = 'ERROR',
  PRIVATE_POSTS = 'PRIVATE_POSTS',
  UPGRADE_PREMIUM = 'UPGRADE_PREMIUM',
  UNPUBLISHED_POST = 'UNPUBLISHED_POST',
  NO_GUIDELINES = 'NO_GUIDELINES',
  PERMISSION_PENDING = 'PERMISSION_PENDING',
}

const GUIDELINES_WHERE_REFRESH_CANNOT_HELP: SocialPostUserGuidelines[] = [
  SocialPostUserGuidelines.UNPUBLISHED_POST,
  SocialPostUserGuidelines.PERFORMANCE_LIMITATIONS,
  SocialPostUserGuidelines.STORY_PERFORMANCE_LIMITATIONS,
  SocialPostUserGuidelines.STORY_INSIGHTS_EXPIRED,
  SocialPostUserGuidelines.INSIGHTS_WINDOW_CLOSED,
  SocialPostUserGuidelines.ERROR,
  SocialPostUserGuidelines.PRIVATE_POSTS,
  SocialPostUserGuidelines.UPGRADE_PREMIUM,
  SocialPostUserGuidelines.PERMISSION_PENDING,
  SocialPostUserGuidelines.POST_DELETED_ON_PLATFORM,
  SocialPostUserGuidelines.METRICS_UNAVAILABLE,
];

/**
 * Guideline types that should still render the metrics grid alongside the guideline message.
 * PARTIAL keeps the aggregate sum visible while flagging missing accounts; INSIGHTS_WINDOW_CLOSED
 * surfaces the final captured numbers (e.g. IG/FB Stories past their 24h window).
 */
const shouldRenderGridAlongsideGuideline = (userGuidelineType: SocialPostUserGuidelines): boolean => {
  return userGuidelineType === SocialPostUserGuidelines.PARTIAL || userGuidelineType === SocialPostUserGuidelines.INSIGHTS_WINDOW_CLOSED;
};

export interface MetricMeta {
  value: number;
  type: AllMetricKeys;
}

export function SocialPostDialogMetricsGrid({activeAccountIndex, socialPost}: SocialPostDialogMetricsGridProps): ReactElement<any> {
  const numberMetricsPerRow = 2;
  const userGuidelineType = useUserGuidelineType();
  const [socialPostState, setSocialPostState] = useState<SocialPost>(socialPost);
  const [metricsUpdateLoadingState, setMetricsUpdateLoadingState] = useState<LoadingStates>(LoadingStates.NOT_LOADED);
  const dispatch = useAppDispatch();
  const [cooldownSecondsRemaining, setCooldownSecondsRemaining] = useState<number>(0);
  const [isDailyCapExceeded, setIsDailyCapExceeded] = useState<boolean>(false);
  const updateTextRef = useRef<HTMLAnchorElement | HTMLSpanElement>(null);
  const metricsErrorTextRef = useRef<HTMLAnchorElement | HTMLSpanElement>(null);

  const isCooldownActive = cooldownSecondsRemaining > 0;
  const areMetricsLoading = metricsUpdateLoadingState === LoadingStates.LOADING;
  const isRefreshDisabled = areMetricsLoading || isCooldownActive || isDailyCapExceeded;

  // Keep the open dialog in sync with background grid auto-refreshes: when fresh
  // metrics land for this post, the grid dispatches updatePostAfterMetricsRefresh,
  // so mirror the slice's post into local state instead of showing stale numbers.
  const dialogPostFromStore = useAppSelector((state) => state.socialPostDialog.post);
  useDidUpdateEffect((): void => {
    if (!dialogPostFromStore) {
      return;
    }
    if (dialogPostFromStore.id === socialPostState.id) {
      setSocialPostState(dialogPostFromStore);
    }
  }, [dialogPostFromStore]);

  useEffect((): (() => void) | void => {
    if (!isCooldownActive) {
      return;
    }

    const intervalId = window.setInterval((): void => {
      setCooldownSecondsRemaining((prev: number): number => Math.max(0, prev - 1));
    }, 1000);

    return (): void => {
      window.clearInterval(intervalId);
    };
  }, [isCooldownActive]);

  useEffect((): (() => void) => {
    const channel = addConnectAccountBroadcastChannel((broadcastEvent: MessageEvent): void => {
      const connectionResponse = getAccountConnectionResponseFromBroadcast(broadcastEvent);
      handleAccountConnectionResponse(connectionResponse, (): void => {
        void refreshPostAfterReauth();
      });
    });
    return (): void => {
      removeConnectAccountBroadcastChannel(channel);
    };
  }, [socialPostState.id]);

  const refreshPostAfterReauth = async (): Promise<void> => {
    try {
      const response = (await window.PMW.writeLocal('socialmedia/enablePostMetrics', {postId: socialPostState.id})) as EnablePostMetricsResponse | null;
      if (!response?.post) {
        flashInlineMessage(window.i18next.t('pmwjs_something_went_wrong'));
        return;
      }
      setSocialPostState(response.post);
      dispatch(updatePostAfterMetricsRefresh(response.post));
    } catch (error: unknown) {
      flashInlineMessage(error instanceof Error ? error.message : window.i18next.t('pmwjs_something_went_wrong'));
    }
  };

  const getFlatIconColor = (): FlatIconColor => {
    if (isDefaultSelection() && socialPostState.accounts.length > 1) {
      return FlatIconColor.SECONDARY;
    }

    return FlatIconColor.PRIMARY;
  };

  const isMultiPosting = (): boolean => {
    return socialPostState.designs.length > 1;
  };

  const shouldShowReelMetricsForInstagram = (): boolean => {
    if (!isMultiPosting()) {
      return false;
    }

    const {posterHashedId, pageHashedId} = socialPostState.designs[0];
    const publishingParamsInformation = mapIndexToAccountPublishingParams(socialPostState, activeAccountIndex);
    const {account} = publishingParamsInformation;
    const {platformMediaType} = publishingParamsInformation;
    const graphicsMetaForPoster = socialPostState.publishingParams[account.id][platformMediaType];

    return !!graphicsMetaForPoster?.graphicsMetaMap[posterHashedId][pageHashedId].isVideo;
  };

  const getInstagramVariant = (): InstagramVariant => {
    const {platformMediaType} = mapIndexToAccountPublishingParams(socialPostState, activeAccountIndex);
    if (platformMediaType === PlatformMediaType.STORIES) {
      return 'story';
    }
    if (shouldShowReelMetricsForInstagram()) {
      return 'reel';
    }
    return 'image';
  };

  const getFacebookVariant = (): FacebookVariant => {
    const {platformMediaType} = mapIndexToAccountPublishingParams(socialPostState, activeAccountIndex);
    return platformMediaType === PlatformMediaType.STORIES ? 'story' : 'image';
  };

  const getActiveMetricsOrEmpty = (accountId: number, platformMediaType: PlatformMediaType): SocialPlatformMetrics => {
    return getMetricsForAccount(socialPostState, accountId, platformMediaType) ?? EMPTY_METRICS;
  };

  const getBaseMetrics = (metricIndex: number): number => {
    return sumBaseMetricsAcrossAllAccounts(socialPostState, metricsAggregate[metricIndex]);
  };

  const getMetricMeta = (metricIndex: number): MetricMeta => {
    const publishingParamsInformation = mapIndexToAccountPublishingParams(socialPostState, activeAccountIndex);
    const {account, platformMediaType} = publishingParamsInformation;

    if (isDefaultSelection() && socialPostState.accounts.length > 1) {
      return {
        value: getBaseMetrics(metricIndex),
        type: undefined,
      };
    }

    const socialPostMetrics = getActiveMetricsOrEmpty(account.id, platformMediaType);

    switch (account.type) {
      case SocialPlatform.FACEBOOK_PROFILE:
      case SocialPlatform.FACEBOOK:
      case SocialPlatform.FACEBOOK_PAGE:
      case SocialPlatform.FACEBOOK_GROUP: {
        const fbVariant = getFacebookVariant();
        const fbKeys = fbVariant === 'story' ? metricsFacebookStory : metricsFacebook;
        return {
          value: getFacebookMetricsByIndex(socialPostMetrics, metricIndex, fbVariant),
          type: fbKeys[metricIndex],
        };
      }

      case SocialPlatform.TWITTER:
        return {
          value: getTwitterMetricsByIndex(socialPostMetrics, metricIndex),
          type: metricsTwitter[metricIndex],
        };

      case SocialPlatform.INSTAGRAM: {
        const igVariant = getInstagramVariant();
        return {
          value: getInstagramMetricsByIndex(socialPostMetrics, metricIndex, igVariant),
          type: getInstagramMetricsKey(igVariant, metricIndex),
        };
      }

      case SocialPlatform.TIKTOK:
        return {
          value: getTikTokMetricsByIndex(socialPostMetrics, metricIndex),
          type: metricsTikTok[metricIndex],
        };

      case SocialPlatform.YOUTUBE:
        return {
          value: getYouTubeMetricsByIndex(socialPostMetrics, metricIndex),
          type: metricsYouTube[metricIndex],
        };

      case SocialPlatform.LINKEDIN:
      case SocialPlatform.LINKEDIN_PAGE:
      case SocialPlatform.LINKEDIN_PROFILE:
        return {
          value: getLinkedinMetricsByIndex(socialPostMetrics, metricIndex),
          type: metricsLinkedInPage[metricIndex],
        };

      case SocialPlatform.PINTEREST:
        return {
          value: getPinterestMetricsByIndex(socialPostMetrics, metricIndex),
          type: metricsPinterest[metricIndex],
        };

      case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
        return {
          value: getGoogleBusinessProfileMetricsByIndex(socialPostMetrics, metricIndex),
          type: metricsGoogleBusinessProfile[metricIndex],
        };

      case SocialPlatform.THREADS:
        return {
          value: getThreadsMetricsByIndex(socialPostMetrics, metricIndex),
          type: metricsThreads[metricIndex],
        };

      default:
        return {
          value: 0,
          type: undefined,
        };
    }
  };

  const isDefaultSelection = (): boolean => {
    return activeAccountIndex === -1;
  };

  const getMetricIcon = (metricIndex: number): string => {
    if (isDefaultSelection() && socialPostState.accounts.length > 1) {
      return getBaseMetricIconByIndex(metricIndex);
    }

    const publishingParamsInformation = mapIndexToAccountPublishingParams(socialPostState, activeAccountIndex);
    const {account} = publishingParamsInformation;

    switch (account.type) {
      case SocialPlatform.FACEBOOK_PROFILE:
      case SocialPlatform.FACEBOOK:
      case SocialPlatform.FACEBOOK_PAGE:
      case SocialPlatform.FACEBOOK_GROUP:
        return getFacebookMetricIconByIndex(metricIndex, getFacebookVariant());

      case SocialPlatform.TWITTER:
        return getTwitterMetricIconByIndex(metricIndex);

      case SocialPlatform.INSTAGRAM:
        return getInstagramMetricIconByIndex(metricIndex, getInstagramVariant());

      case SocialPlatform.TIKTOK:
        return getTikTokMetricIconByIndex(metricIndex);

      case SocialPlatform.YOUTUBE:
        return getYouTubeMetricIconByIndex(metricIndex);

      case SocialPlatform.LINKEDIN:
      case SocialPlatform.LINKEDIN_PROFILE:
      case SocialPlatform.LINKEDIN_PAGE:
        return getLinkedinMetricIconByIndex(metricIndex);

      case SocialPlatform.PINTEREST:
        return getPinterestMetricIconByIndex(metricIndex);

      case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
        return getGoogleBusinessProfileMetricIconByIndex(metricIndex);

      case SocialPlatform.THREADS:
        return getThreadsMetricIconByIndex(metricIndex);

      default:
        return '';
    }
  };

  const getMetricText = (metricIndex: number): string => {
    if (isDefaultSelection() && socialPostState.accounts.length > 1) {
      return getBaseMetricDescriptionByIndex(metricIndex);
    }

    const publishingParamsInformation = mapIndexToAccountPublishingParams(socialPostState, activeAccountIndex);
    const {account} = publishingParamsInformation;

    switch (account.type) {
      case SocialPlatform.FACEBOOK_PROFILE:
      case SocialPlatform.FACEBOOK:
      case SocialPlatform.FACEBOOK_PAGE:
      case SocialPlatform.FACEBOOK_GROUP:
        return getFacebookMetricDescriptionByIndex(metricIndex, getFacebookVariant());

      case SocialPlatform.TWITTER:
        return getTwitterMetricDescriptionByIndex(metricIndex);

      case SocialPlatform.INSTAGRAM:
        return getInstagramDescriptionByIndex(metricIndex, getInstagramVariant());

      case SocialPlatform.TIKTOK:
        return getTikTokMetricDescriptionByIndex(metricIndex);

      case SocialPlatform.YOUTUBE:
        return getYouTubeMetricDescriptionByIndex(metricIndex);

      case SocialPlatform.LINKEDIN:
      case SocialPlatform.LINKEDIN_PROFILE:
      case SocialPlatform.LINKEDIN_PAGE:
        return getLinkedInMetricDescriptionByIndex(metricIndex);

      case SocialPlatform.PINTEREST:
        return getPinterestMetricDescriptionByIndex(metricIndex);

      case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
        return getGoogleBusinessProfileMetricDescriptionByIndex(metricIndex);

      case SocialPlatform.THREADS:
        return getThreadsMetricDescriptionByIndex(metricIndex);

      default:
        return '';
    }
  };

  const getIconTooltipByAccountSelection = (metricIndex: number): string => {
    const publishingParamsInformation = mapIndexToAccountPublishingParams(socialPostState, activeAccountIndex);
    const {account} = publishingParamsInformation;

    if (isDefaultSelection() && socialPostState.accounts.length > 1) {
      return getBaseIconTooltipByIndex(metricIndex);
    }

    switch (account.type) {
      case SocialPlatform.FACEBOOK:
      case SocialPlatform.FACEBOOK_PROFILE:
      case SocialPlatform.FACEBOOK_PAGE:
      case SocialPlatform.FACEBOOK_GROUP:
        return getFacebookIconTooltipByIndex(metricIndex, getFacebookVariant());
      case SocialPlatform.INSTAGRAM:
        return getInstagramIconTooltipByIndex(metricIndex, getInstagramVariant());
      case SocialPlatform.YOUTUBE:
        return getYouTubeIconTooltipByIndex(metricIndex);
      case SocialPlatform.TIKTOK:
        return getTikTokIconTooltipByIndex(metricIndex);
      case SocialPlatform.LINKEDIN:
      case SocialPlatform.LINKEDIN_PROFILE:
      case SocialPlatform.LINKEDIN_PAGE:
        return getLinkedInIconTooltipByIndex(metricIndex);
      case SocialPlatform.TWITTER:
        return getTwitterIconTooltipByIndex(metricIndex);
      case SocialPlatform.PINTEREST:
        return getPinterestIconTooltipByIndex(metricIndex);
      case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
        return getGoogleBusinessProfileIconTooltipByIndex(metricIndex);
      case SocialPlatform.THREADS:
        return getThreadsIconTooltipByIndex(metricIndex);
      default:
        return '';
    }
  };

  const getMetricIconTooltip = (metricIndex: number): ReactElement<any> => {
    const icon = (
      <Icon
        type={IconType.FLAT}
        size={IconSize.SIZE_ICON_20}
        className={isDefaultSelection() && socialPostState.accounts.length > 1 ? styles.iconStyles : ''}
        flatIconType={{color: getFlatIconColor(), type: FlatIconColorType.DEFAULT}}
        shape={IconShape.CIRCLE}
        icon={getMetricIcon(metricIndex)}
      />
    );

    return (
      <OverlayTrigger
        trigger={['hover', 'focus']}
        placement="top"
        delay={{show: 50, hide: 100}}
        overlay={
          <Tooltip id="tooltip-icon" className="spacing-m-b-3 _pointer-events-none">
            {getIconTooltipByAccountSelection(metricIndex)}
          </Tooltip>
        }
        show={undefined}
        defaultShow={undefined}
        flip={undefined}
        onHide={undefined}
        onToggle={undefined}
        popperConfig={undefined}
        target={undefined}
      >
        {icon}
      </OverlayTrigger>
    );
  };

  const getAccountWiseTooltip = (metricIndex: number): ReactElement<any> => {
    const metricKey = metricsAggregate[metricIndex];
    const accountMetricsList: ReactElement<any>[] = [];

    socialPostState.accounts.forEach((account) => {
      if (Object.keys(socialPostState.metrics?.[account.id] ?? {}).length === 0) {
        return;
      }
      const num = sumBaseMetricForAccount(socialPostState, account.id, metricKey);
      const detail = `${getReadableAccounts(account.type)}: ${getTooltipReadableMetrics(num)}`;

      accountMetricsList.push(
        <div className={styles.accountTooltip} id={`account-breakdown-${account.id}-${metricIndex}`}>
          {detail}
        </div>
      );
    });

    return (
      <div id={`tooltip-${metricIndex}`} className={styles.tooltipContainer}>
        {accountMetricsList}
      </div>
    );
  };

  const shouldShowNA = (metricIndex: number): boolean => {
    const likeIndex = 0;
    const commentIndex = 1;
    const shareIndex = 4;

    if (isDefaultSelection() && socialPostState.accounts.length !== 1) {
      return false;
    }

    const publishingParamsInformation = mapIndexToAccountPublishingParams(socialPostState, activeAccountIndex);
    const {account} = publishingParamsInformation;

    const isInstagramMultiPosting = account.type === SocialPlatform.INSTAGRAM && isMultiPosting();

    if (!isInstagramMultiPosting) {
      return false;
    }

    return metricIndex === likeIndex || metricIndex === commentIndex || metricIndex === shareIndex;
  };

  const getIsMetricPending = (metricMeta: MetricMeta): boolean => {
    if (isDefaultSelection() && socialPostState.accounts.length > 1) {
      return false;
    }
    const {account} = mapIndexToAccountPublishingParams(socialPostState, activeAccountIndex);
    return isMetricPending(account.type, metricMeta.type, metricMeta.value, socialPostState.publishOn);
  };

  const getMetricValueTooltip = (readableMetric: string, metricIndex: number, isPending: boolean): ReactElement<any> => {
    const displayValue = !isPending && shouldShowNA(metricIndex) ? window.i18next.t('pmwjs_na') : readableMetric;
    const numberText = <Text val={displayValue} bold size={TextSize.SMALL} className="content-body" />;
    const subText = <Text val={getMetricText(metricIndex)} size={TextSize.XXSMALL} className="content-sub-text" />;

    if (isPending) {
      return (
        <div className={styles.metricDetailsContainer}>
          <OverlayTrigger
            trigger={['hover', 'focus']}
            placement="top"
            delay={{show: 50, hide: 100}}
            overlay={
              <Tooltip id="tooltip-pending" className="spacing-m-b-3 _pointer-events-none">
                {window.i18next.t('pmwjs_metric_pending_tooltip')}
              </Tooltip>
            }
            show={undefined}
            defaultShow={undefined}
            flip={undefined}
            onHide={undefined}
            onToggle={undefined}
            popperConfig={undefined}
            target={undefined}
          >
            <div className={styles.metricNumberContainer}>{numberText}</div>
          </OverlayTrigger>
          {subText}
        </div>
      );
    }

    const metricElementDefault = (
      <div className={styles.metricDetailsContainer}>
        <OverlayTrigger
          trigger={['hover', 'focus']}
          placement="top"
          delay={{show: 50, hide: 100}}
          overlay={
            <Tooltip id="tooltip-icon" className="spacing-m-b-2 _pointer-events-none">
              {getAccountWiseTooltip(metricIndex)}
            </Tooltip>
          }
          show={undefined}
          defaultShow={undefined}
          flip={undefined}
          onHide={undefined}
          onToggle={undefined}
          popperConfig={undefined}
          target={undefined}
        >
          <div className={styles.metricNumberContainer}>{numberText}</div>
        </OverlayTrigger>
        {subText}
      </div>
    );

    if (isDefaultSelection() && socialPostState.accounts.length !== 1) {
      return metricElementDefault;
    }

    return (
      <div className={styles.metricDetailsContainer}>
        {numberText}
        {subText}
      </div>
    );
  };

  const getMetric = (metricIndex: number): ReactElement<any> => {
    const metricMeta = getMetricMeta(metricIndex);
    const isPending = getIsMetricPending(metricMeta);
    const readableMetric = isPending ? PENDING_METRIC_PLACEHOLDER : getReadableMetrics(metricMeta);
    const pendingClass = isPending ? ` ${styles.pendingMetric}` : '';

    return (
      <div key={`metric-container-${metricIndex}`} className={`${styles.metricWrapper}${pendingClass} _fit-width _fit-height`}>
        {getMetricIconTooltip(metricIndex)}
        {getMetricValueTooltip(readableMetric, metricIndex, isPending)}
      </div>
    );
  };

  const getRowByIndex = (index: number): ReactElement<any> => {
    const row = [];

    for (let j = 0; j < numberMetricsPerRow; j++) {
      const metricsIndex = numberMetricsPerRow * index + j;
      if (metricsIndex < getNumberMetrics()) {
        const metric = getMetric(metricsIndex);
        row.push(metric);
      }
    }

    return (
      <div key={`metrics-row-${index}`} className={styles.metricsGridRow}>
        {row}
      </div>
    );
  };

  const getNumberMetrics = (): number => {
    if (isDefaultSelection() && socialPostState.accounts.length > 1) {
      return metricsAggregate.length;
    }

    const publishingParamsInformation = mapIndexToAccountPublishingParams(socialPostState, activeAccountIndex);
    const {account} = publishingParamsInformation;

    switch (account.type) {
      case SocialPlatform.FACEBOOK_PROFILE:
      case SocialPlatform.FACEBOOK:
      case SocialPlatform.FACEBOOK_PAGE:
      case SocialPlatform.FACEBOOK_GROUP:
        return getFacebookVariant() === 'story' ? metricsFacebookStory.length : metricsFacebook.length;

      case SocialPlatform.TWITTER:
        return metricsTwitter.length;

      case SocialPlatform.INSTAGRAM: {
        const igVariant = getInstagramVariant();
        if (igVariant === 'story') {
          return metricsInstagramStory.length;
        }
        if (igVariant === 'reel') {
          return metricsInstagramVideo.length;
        }
        return metricsInstagramPost.length;
      }

      case SocialPlatform.LINKEDIN_PAGE:
        return metricsLinkedInPage.length;

      case SocialPlatform.PINTEREST:
        return metricsPinterest.length;

      case SocialPlatform.YOUTUBE:
        return metricsYouTube.length;

      case SocialPlatform.TIKTOK:
        return metricsTikTok.length;

      case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
        return metricsGoogleBusinessProfile.length;

      case SocialPlatform.THREADS:
        return metricsThreads.length;

      default:
        return 0;
    }
  };

  const getGrid = (): ReactElement<any>[] => {
    const nMetrics = getNumberMetrics();
    const nRows = ceil(nMetrics / numberMetricsPerRow);
    const rows = [];

    for (let i = 0; i < nRows; i++) {
      const row = getRowByIndex(i);
      rows.push(row);
    }

    return rows;
  };

  const shouldShowMetrics = (): boolean => {
    const publishingParamsInformation = mapIndexToAccountPublishingParams(socialPostState, activeAccountIndex);
    const {account, platformMediaType} = publishingParamsInformation;

    if (!socialPostState.metrics) {
      return false;
    }

    if (isDefaultSelection()) {
      return true;
    }

    return hasMetricsForAccountMediaType(socialPostState, account.id, platformMediaType);
  };

  const getMetricsGrid = (): ReactElement<any> => {
    return <div className={styles.metricsGrid}>{getGrid()}</div>;
  };

  const getIconName = (guidelineType: SocialPostUserGuidelines): string => {
    switch (guidelineType) {
      case SocialPostUserGuidelines.ERROR:
        return 'icon-exclamation-triangle';

      case SocialPostUserGuidelines.PERMISSION_PENDING:
        return 'icon-ban';

      case SocialPostUserGuidelines.UPGRADE_PREMIUM:
        return 'icon-bar-chart';

      case SocialPostUserGuidelines.PRIVATE_POSTS:
        return 'icon-globe';

      case SocialPostUserGuidelines.INSIGHTS_WINDOW_CLOSED:
        return 'icon-clock-o';

      default:
        return 'icon-bar-chart';
    }
  };

  const getFlatIconType = (guidelineType: SocialPostUserGuidelines): FlatIconType => {
    switch (guidelineType) {
      case SocialPostUserGuidelines.ERROR:
        return {color: FlatIconColor.DANGER, type: FlatIconColorType.DEFAULT};

      case SocialPostUserGuidelines.UPGRADE_PREMIUM:
        return {
          color: FlatIconColor.PREMIUM,
          type: FlatIconColorType.DEFAULT,
        };

      default:
        return {
          color: FlatIconColor.NEUTRAL,
          type: FlatIconColorType.DEFAULT,
        };
    }
  };

  const getUserGuidelineIcon = (guidelineType: SocialPostUserGuidelines): ReactElement<any> => {
    if (
      userGuidelineType === SocialPostUserGuidelines.PERFORMANCE_LIMITATIONS ||
      userGuidelineType === SocialPostUserGuidelines.STORY_PERFORMANCE_LIMITATIONS ||
      userGuidelineType === SocialPostUserGuidelines.STORY_INSIGHTS_EXPIRED
    ) {
      return (
        <div className={styles.emojiContainer}>
          <Text val={window.i18next.t('pmwjs_performance_metrics_performance_limitation_emoji')} />
        </div>
      );
    }

    return (
      <Icon
        className="spacing-m-b-3"
        icon={getIconName(guidelineType)}
        shape={IconShape.CIRCLE}
        size={IconSize.SIZE_ICON_24}
        type={IconType.FLAT}
        flatIconType={getFlatIconType(guidelineType)}
      />
    );
  };

  const getPermissionPendingTypeText = (currentAccount: SocialAccount): string => {
    if (isDefaultSelection()) {
      return window.i18next.t('pmwjs_performance_metrics_permission_required_general');
    }

    return window.i18next.t('pmwjs_performance_metrics_permission_required', {platform: getReadableAccounts(currentAccount.type)});
  };

  const getUserGuidelineText = (guidelineType: SocialPostUserGuidelines): string => {
    const publishingParamsInformation = mapIndexToAccountPublishingParams(socialPostState, activeAccountIndex);
    const {account} = publishingParamsInformation;
    const {platformMediaType} = publishingParamsInformation;

    if (!socialPostState.publishingParams[account.id]) {
      return '';
    }

    const currentAccount = account;
    const currentPublishingParams = socialPostState.publishingParams[currentAccount.id][platformMediaType];

    if (!currentPublishingParams) {
      return '';
    }

    switch (guidelineType) {
      case SocialPostUserGuidelines.ERROR:
        return currentPublishingParams.publishErrorMessage ?? window.i18next.t('pmwjs_account_expired_guideline');
      case SocialPostUserGuidelines.PRIVATE_POSTS:
        return window.i18next.t('pmwjs_performance_metrics_private_posts');
      case SocialPostUserGuidelines.POPULATING:
        return window.i18next.t('pmwjs_performance_metrics_populating', {platform: getReadableAccounts(currentAccount.type)});
      case SocialPostUserGuidelines.POPULATING_FRESH:
        return window.i18next.t('pmwjs_metrics_populating_fresh');
      case SocialPostUserGuidelines.POPULATING_DELAYED:
        return window.i18next.t('pmwjs_metrics_populating_delayed');
      case SocialPostUserGuidelines.PARTIAL:
        return window.i18next.t('pmwjs_metrics_partial', {
          availableCount: countAccountsWithAnyMetrics(socialPostState),
          totalCount: socialPostState.accounts.length,
        });
      case SocialPostUserGuidelines.POST_DELETED_ON_PLATFORM:
        return window.i18next.t('pmwjs_metrics_post_deleted_platform', {
          platforms: getPlatformsForAvailability(socialPostState, MetricsAvailability.DELETED_ON_PLATFORM),
        });
      case SocialPostUserGuidelines.METRICS_TEMPORARILY_UNAVAILABLE:
        return window.i18next.t('pmwjs_metrics_temporarily_unavailable', {
          platform: getReadableAccounts(currentAccount.type),
        });
      case SocialPostUserGuidelines.METRICS_UNAVAILABLE:
        return window.i18next.t('pmwjs_metrics_exhausted');
      case SocialPostUserGuidelines.MIXED_UNAVAILABLE:
        return window.i18next.t('pmwjs_metrics_mixed_unavailable');
      case SocialPostUserGuidelines.UPGRADE_PREMIUM:
        return window.i18next.t('pmwjs_performance_metrics_upgrade');
      case SocialPostUserGuidelines.PERMISSION_PENDING:
        return getPermissionPendingTypeText(currentAccount);
      case SocialPostUserGuidelines.UNPUBLISHED_POST:
        return window.i18next.t('pmwjs_performance_metrics_published');
      case SocialPostUserGuidelines.PERFORMANCE_LIMITATIONS:
        return window.i18next.t('pmwjs_performance_metrics_performance_limitations', {platform: getReadableAccounts(currentAccount.type)});
      case SocialPostUserGuidelines.STORY_PERFORMANCE_LIMITATIONS:
        return window.i18next.t('pmwjs_performance_metrics_story_performance_limitations');
      case SocialPostUserGuidelines.STORY_INSIGHTS_EXPIRED:
        return window.i18next.t('pmwjs_metrics_story_insights_expired');
      case SocialPostUserGuidelines.INSIGHTS_WINDOW_CLOSED:
        return window.i18next.t('pmwjs_metrics_insights_window_closed');
      default:
        return '';
    }
  };

  const shouldShowAccountBreakdown = (): boolean => {
    return userGuidelineType === SocialPostUserGuidelines.PARTIAL || userGuidelineType === SocialPostUserGuidelines.MIXED_UNAVAILABLE;
  };

  const renderAccountBreakdown = (): ReactElement<any> | null => {
    if (!shouldShowAccountBreakdown()) {
      return null;
    }

    return <SocialPostMetricsPerAccountBreakdown post={socialPostState} />;
  };

  const handleGrantAccessClick = (): void => {
    const {account} = mapIndexToAccountPublishingParams(socialPostState, activeAccountIndex);
    reAuthenticateInModal(account.id, account.type);
  };

  const renderGrantAccessLink = (): ReactElement<any> | null => {
    if (userGuidelineType !== SocialPostUserGuidelines.PERMISSION_PENDING || (isDefaultSelection() && socialPostState.accounts.length > 1)) {
      return null;
    }

    return (
      <Text className={`${styles.updateNowText} content-link`} size={TextSize.XXSMALL} val={window.i18next.t('pmwjs_metrics_grant_access_link')} onClick={handleGrantAccessClick} />
    );
  };

  const getUserGuideline = (): ReactElement<any> => {
    return (
      <div className={`${styles.userGuidelineContainer} _full-width`}>
        {getUserGuidelineIcon(userGuidelineType)}
        <Text className={styles.guidelineStyles} size={TextSize.XSMALL} val={getUserGuidelineText(userGuidelineType)} />
        {renderGrantAccessLink()}
        {renderAccountBreakdown()}
      </div>
    );
  };

  const getPerformanceHeading = (): ReactElement<any> => {
    return <Text val={window.i18next.t('pmwjs_performance')} className={`${shouldShowMetrics() ? 'spacing-m-b-3' : ''} content-body`} bold size={TextSize.SMALL} />;
  };

  const shouldShowRefreshControl = (): boolean => {
    return !GUIDELINES_WHERE_REFRESH_CANNOT_HELP.includes(userGuidelineType);
  };

  const getLastUpdatedOn = (): ReactElement<any> | null => {
    if (!shouldShowRefreshControl()) {
      return null;
    }

    return (
      <div className={'flex-v-row gap-2'}>
        {getUpdateNowRow()}
        <Text val={''} className={`content-danger flex-center text-center`} size={TextSize.XXSMALL} ref={metricsErrorTextRef} />
      </div>
    );
  };

  const getUpdateNowTextClasses = (): string => {
    const disabledClassName = isRefreshDisabled ? styles.disabledText : '';

    return `${styles.updateNowText} ${disabledClassName} content-link`;
  };

  const getUpdateNowLinkLabel = (): string => {
    if (isCooldownActive) {
      return window.i18next.t('pmwjs_metrics_refresh_cooldown', {seconds: cooldownSecondsRemaining});
    }

    if (areMetricsLoading) {
      return window.i18next.t('pmwjs_updating');
    }

    return window.i18next.t('pmwjs_update_now');
  };

  const getUpdateNowRow = (): ReactElement<any> => {
    const lastUpdatedLabel = socialPostState.metricsUpdatedOn
      ? window.i18next.t('pmwjs_last_updated_on', {timeDetails: getTimeToOrFrom(getDateFromUnixTimestamp(socialPostState.metricsUpdatedOn))})
      : '';

    return (
      <div className={styles.lastUpdatedContainer}>
        {lastUpdatedLabel !== '' ? <Text val={lastUpdatedLabel} className={`content-sub-text`} size={TextSize.XXSMALL} /> : null}
        <Text
          disableSelection={isRefreshDisabled}
          val={getUpdateNowLinkLabel()}
          className={getUpdateNowTextClasses()}
          size={TextSize.XXSMALL}
          onClick={fetchFreshMetrics}
          ref={updateTextRef}
        />
      </div>
    );
  };

  const flashInlineMessage = (message: string): void => {
    if (!metricsErrorTextRef.current) {
      return;
    }

    metricsErrorTextRef.current.innerText = message;

    window.setTimeout((): void => {
      if (metricsErrorTextRef.current) {
        metricsErrorTextRef.current.innerText = '';
      }
    }, REFRESH_RESULT_FLASH_DURATION_MS);
  };

  const fetchFreshMetrics = (): void => {
    void doFetchFreshMetrics();
  };

  const doFetchFreshMetrics = async (): Promise<void> => {
    if (isRefreshDisabled) {
      return;
    }

    setMetricsUpdateLoadingState(LoadingStates.LOADING);

    try {
      const response = (await window.PMW.readLocal(`socialmedia/populateMetricsForPost/${socialPostState.id}`)) as ManualMetricsRefreshResponse | null;
      if (response) {
        handleManualRefreshResponse(response);
      }
    } catch (e: unknown) {
      flashInlineMessage(e instanceof Error ? e.message : window.i18next.t('pmwjs_something_went_wrong'));
      console.error(e);
    }

    setMetricsUpdateLoadingState(LoadingStates.LOADED);
  };

  const handleManualRefreshResponse = (response: ManualMetricsRefreshResponse): void => {
    switch (response.result) {
      case ManualMetricsRefreshResult.REFRESHED:
        if (response.post) {
          setSocialPostState(response.post);
          dispatch(updatePostAfterMetricsRefresh(response.post));
        }
        setCooldownSecondsRemaining(response.cooldownSecondsRemaining);
        flashInlineMessage(window.i18next.t('pmwjs_metrics_refresh_succeeded'));
        return;

      case ManualMetricsRefreshResult.FRESH_ENOUGH:
        if (response.post) {
          setSocialPostState(response.post);
          dispatch(updatePostAfterMetricsRefresh(response.post));
        }
        flashInlineMessage(window.i18next.t('pmwjs_metrics_refresh_fresh'));
        return;

      case ManualMetricsRefreshResult.COOLDOWN:
        setCooldownSecondsRemaining(response.cooldownSecondsRemaining);
        return;

      case ManualMetricsRefreshResult.DAILY_CAP_EXCEEDED:
        setIsDailyCapExceeded(true);
        flashInlineMessage(window.i18next.t('pmwjs_metrics_refresh_daily_cap'));
        return;

      case ManualMetricsRefreshResult.UNAVAILABLE:
      default:
        flashInlineMessage(window.i18next.t('pmwjs_metrics_exhausted'));
        return;
    }
  };

  const renderRefreshLoadingSkeleton = (): ReactElement<any> => {
    if (userGuidelineType === SocialPostUserGuidelines.NO_GUIDELINES) {
      return (
        <div className={styles.metricsGrid}>
          <div className={styles.metricsGridRow}>
            <SkeletonBrick width={150} height={48} radius="8px" />
            <SkeletonBrick width={150} height={48} radius="8px" />
          </div>
          <div className={styles.metricsGridRow}>
            <SkeletonBrick width={150} height={48} radius="8px" />
            <SkeletonBrick width={150} height={48} radius="8px" />
          </div>
        </div>
      );
    }

    return (
      <div className={`${styles.userGuidelineContainer} _full-width`}>
        <SkeletonBrick className="spacing-m-b-3" width={48} height={48} radius="50%" />
        <SkeletonBrick className="spacing-m-b-2" width={240} height={12} radius="4px" />
        <SkeletonBrick width={180} height={12} radius="4px" />
      </div>
    );
  };

  const renderMainContent = (): ReactElement<any> => {
    if (areMetricsLoading) {
      return renderRefreshLoadingSkeleton();
    }

    if (userGuidelineType === SocialPostUserGuidelines.NO_GUIDELINES) {
      return getMetricsGrid();
    }

    if (shouldRenderGridAlongsideGuideline(userGuidelineType)) {
      return (
        <>
          {getMetricsGrid()}
          {getUserGuideline()}
        </>
      );
    }

    return getUserGuideline();
  };

  return (
    <div className={`${styles.metricsWrapper} _full-width _full-height`}>
      {userGuidelineType === SocialPostUserGuidelines.ERROR ? null : getPerformanceHeading()}
      {renderMainContent()}
      {getLastUpdatedOn()}
    </div>
  );
}

export default SocialPostDialogMetricsGrid;
